import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

export type ArticleAudience = "General" | "Teens" | "Parents" | "Beginners"
export type ArticleJobStatus = "pending" | "processing" | "completed" | "failed"

export type ArticleSource = {
  name: string
  title: string
  url: string | null
}

export type ArticleGenerateRequest = {
  primary_topic: string
  ad_text: string
  countries: string[]
  language: string
  target_audience?: ArticleAudience
}

export type ArticleGenerateStartResponse = {
  archive_id: number
  status: ArticleJobStatus
}

export type ArticleGenerateResponse = {
  title: string
  titles: string[]
  ad_text: string
  body_html: string
  body_markdown?: string
  word_count: number
  h2_count: number
  related_searches: string[]
  sources: ArticleSource[]
  provider: string
  readability_band: string
  search_intent: string
  target_audience: ArticleAudience
  archive_id?: number | null
  status?: ArticleJobStatus
  error_message?: string | null
}

export type ArticleArchiveSummary = {
  id: number
  title: string
  language: string
  countries: string[]
  keywords: string[]
  word_count: number
  provider: string
  target_audience: string
  status?: ArticleJobStatus
  created_at: string | null
  created_by: number
  creator_username: string | null
}

export type ArticleArchiveDetail = ArticleArchiveSummary & {
  ad_text: string
  body_html: string
  primary_topic: string
  titles: string[]
  related_searches: string[]
  sources: ArticleSource[]
  h2_count: number
  readability_band: string
  search_intent: string
  error_message?: string | null
}

export type ArticleArchiveListResponse = {
  items: ArticleArchiveSummary[]
  total: number
}

export const ARTICLE_GENERATOR_KEYS = {
  archive: ["article-generator", "archive"] as const,
  archiveItem: (id: number) => ["article-generator", "archive", id] as const,
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Start async generation, then poll until completed/failed. */
async function generateArticleAndWait(
  body: ArticleGenerateRequest,
): Promise<ArticleGenerateResponse> {
  const started = await apiFetch<ArticleGenerateStartResponse>(
    "/api/tools/article-generator/generate",
    { method: "POST", body },
  )

  const startedAt = Date.now()
  const maxMs = 5 * 60 * 1000

  while (Date.now() - startedAt < maxMs) {
    await sleep(2000)
    const job = await apiFetch<ArticleGenerateResponse>(
      `/api/tools/article-generator/jobs/${started.archive_id}`,
    )
    if (job.status === "completed") {
      return { ...job, archive_id: started.archive_id }
    }
    if (job.status === "failed") {
      throw new Error(job.error_message || "Article generation failed")
    }
  }

  throw new Error(
    "Article generation is still running after 5 minutes. Check Archive later.",
  )
}

export function useGenerateArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: generateArticleAndWait,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ARTICLE_GENERATOR_KEYS.archive })
    },
  })
}

export function useArticleArchive(enabled = true) {
  return useQuery({
    queryKey: ARTICLE_GENERATOR_KEYS.archive,
    queryFn: () =>
      apiFetch<ArticleArchiveListResponse>("/api/tools/article-generator/archive"),
    enabled,
  })
}

export function useArticleArchiveItem(id: number | null) {
  return useQuery({
    queryKey: ARTICLE_GENERATOR_KEYS.archiveItem(id ?? 0),
    queryFn: () =>
      apiFetch<ArticleArchiveDetail>(`/api/tools/article-generator/archive/${id}`),
    enabled: id != null && id > 0,
  })
}

export function useDeleteArchivedArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<void>(`/api/tools/article-generator/archive/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ARTICLE_GENERATOR_KEYS.archive })
    },
  })
}
